import React, { useState } from "react";
import { Shield, Zap, Eye, X, Check, Loader, AlertCircle } from "lucide-react";

export type SubscriptionTier = "free" | "spotter" | "tactical" | "ghost";

export interface TierConfig {
  id: SubscriptionTier;
  name: string;
  price: string;
  icon: React.ReactNode;
  badge?: string;
  features: string[];
  aiAnalysis: boolean;
  ttsAudio: boolean;
  dangerAlerts: boolean;
  radiusMiles: number;
  customGeminiKey: boolean;
  gameMode: boolean;
}

export const TIER_CONFIGS: TierConfig[] = [
  {
    id: "spotter",
    name: "SPOTTER",
    price: "$4.99",
    icon: <Eye size={22} />,
    features: [
      "Live police radio feed",
      "Real-time map markers",
      "2-mile threat radius",
      "Full incident history",
      "No session timer",
    ],
    aiAnalysis: false,
    ttsAudio: false,
    dangerAlerts: false,
    radiusMiles: 2,
    customGeminiKey: false,
    gameMode: false,
  },
  {
    id: "tactical",
    name: "TACTICAL",
    price: "$9.99",
    icon: <Shield size={22} />,
    badge: "MOST POPULAR",
    features: [
      "Everything in SPOTTER",
      "AI threat analysis",
      "TTS audio dispatch",
      "Danger zone alerts",
      "5-mile threat radius",
      "Vicinity intel reports",
    ],
    aiAnalysis: true,
    ttsAudio: true,
    dangerAlerts: true,
    radiusMiles: 5,
    customGeminiKey: false,
    gameMode: false,
  },
  {
    id: "ghost",
    name: "GHOST",
    price: "$24.99",
    icon: <Zap size={22} />,
    features: [
      "Everything in TACTICAL",
      "Custom Gemini API key",
      "Game mode + leaderboard",
      "Dev tools access",
      "Ghost Protocol mode",
      "Priority support",
    ],
    aiAnalysis: true,
    ttsAudio: true,
    dangerAlerts: true,
    radiusMiles: 10,
    customGeminiKey: true,
    gameMode: true,
  },
];

interface SubscriptionModalProps {
  userId: string | null;
  currentTier: SubscriptionTier;
  onSubscribed: (tier: SubscriptionTier, subscriptionId: string) => void;
  onClose: () => void;
}

const colorMap: Record<string, string> = { spotter: "amber", tactical: "orange", ghost: "purple" };
const borderActive: Record<string, string> = {
  spotter: "border-amber-500 bg-amber-900/20 shadow-[0_0_30px_rgba(217,119,6,0.25)]",
  tactical: "border-orange-500 bg-orange-900/20 shadow-[0_0_30px_rgba(234,88,12,0.25)]",
  ghost: "border-purple-500 bg-purple-900/20 shadow-[0_0_30px_rgba(147,51,234,0.25)]",
};
const borderIdle: Record<string, string> = {
  spotter: "border-amber-900/40 hover:border-amber-700/60",
  tactical: "border-orange-900/40 hover:border-orange-700/60",
  ghost: "border-purple-900/40 hover:border-purple-700/60",
};
const titleColor: Record<string, string> = { spotter: "text-amber-400", tactical: "text-orange-400", ghost: "text-purple-400" };
const priceColor: Record<string, string> = { spotter: "text-amber-500", tactical: "text-orange-500", ghost: "text-purple-400" };

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ userId, currentTier, onSubscribed, onClose }) => {
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubscribe = async () => {
    if (!selectedTier || !userId) return;
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/api/stripe/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tier: selectedTier, userId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to create checkout");
      if (data.url) {
        window.location.href = data.url; // Redirect to Stripe Checkout
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e: any) {
      setError(e.message || "Something went wrong");
      setLoading(false);
    }
  };

  // Check for successful return from Stripe
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("stripe_success") === "1") {
      const tier = params.get("tier") as SubscriptionTier;
      if (tier && userId) {
        // Verify with backend
        fetch(`/api/stripe/status?userId=${userId}`)
          .then(r => r.json())
          .then(data => {
            if (data.subscribed) {
              onSubscribed(data.tier, data.subscriptionId || "stripe");
            }
          });
        // Clean URL
        const url = new URL(window.location.href);
        url.searchParams.delete("stripe_success");
        url.searchParams.delete("tier");
        window.history.replaceState({}, "", url.pathname);
      }
    }
  }, [userId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md overflow-y-auto">
      <div className="min-h-full flex flex-col items-center justify-start py-10 px-4">
        <div className="relative w-full max-w-4xl text-center mb-8">
          <button onClick={onClose} className="absolute right-0 top-0 text-amber-700 hover:text-amber-400 transition-colors">
            <X size={24} />
          </button>
          <h1 className="font-orbitron text-3xl font-bold text-amber-500 tracking-widest mb-2">TACTICAL INTEL ACCESS</h1>
          <p className="text-amber-700 font-mono text-sm">Choose your clearance level. Billed monthly via Stripe. Cancel anytime.</p>
          {currentTier !== "free" && (
            <p className="mt-2 text-xs font-mono text-amber-600 uppercase tracking-widest">
              Active clearance: <span className="text-amber-400">{currentTier.toUpperCase()}</span>
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-4xl mb-8">
          {TIER_CONFIGS.map((tier) => {
            const isActive = selectedTier === tier.id;
            const isCurrent = currentTier === tier.id;
            return (
              <button
                key={tier.id}
                onClick={() => { setSelectedTier(tier.id); setError(null); }}
                disabled={isCurrent}
                className={`relative p-6 rounded-xl border-2 text-left transition-all ${
                  isActive ? borderActive[tier.id] : borderIdle[tier.id]
                } ${isCurrent ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} bg-black/60`}
              >
                {tier.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-500 text-black text-[10px] font-bold px-3 py-1 rounded-full font-mono uppercase tracking-widest whitespace-nowrap">
                    {tier.badge}
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute -top-3 right-4 bg-green-700 text-white text-[10px] font-bold px-2 py-1 rounded-full font-mono uppercase">ACTIVE</div>
                )}
                <div className={`${titleColor[tier.id]} mb-3`}>{tier.icon}</div>
                <div className={`font-orbitron text-xl font-bold ${titleColor[tier.id]} mb-1`}>{tier.name}</div>
                <div className={`${priceColor[tier.id]} font-mono text-lg font-bold mb-1`}>
                  {tier.price}<span className="text-xs text-amber-800 font-normal">/month</span>
                </div>
                <ul className="mt-4 space-y-2">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-amber-700 font-mono">
                      <Check size={11} className={`${titleColor[tier.id]} mt-0.5 shrink-0`} />
                      {f}
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        {selectedTier && (
          <div className="w-full max-w-sm mx-auto">
            <button
              onClick={handleSubscribe}
              disabled={loading}
              className="w-full bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-black font-bold py-4 rounded-xl transition-all flex items-center justify-center gap-2 font-mono uppercase tracking-widest"
            >
              {loading ? (
                <><Loader size={20} className="animate-spin" /> REDIRECTING TO STRIPE...</>
              ) : (
                <>SUBSCRIBE — {TIER_CONFIGS.find(t => t.id === selectedTier)?.price}/MO</>
              )}
            </button>
            {error && (
              <div className="mt-4 flex items-start gap-2 p-3 bg-red-900/30 border border-red-500/40 rounded-lg text-red-400 text-xs font-mono">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />{error}
              </div>
            )}
          </div>
        )}

        {currentTier !== "free" && (
          <button
            onClick={async () => {
              const res = await fetch("/api/stripe/portal", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userId }) });
              const data = await res.json();
              if (data.url) window.location.href = data.url;
            }}
            className="mt-4 text-amber-700 hover:text-amber-400 text-xs font-mono underline"
          >
            Manage subscription / Cancel
          </button>
        )}

        <p className="mt-8 text-amber-900 text-xs font-mono text-center">
          Secured by Stripe · SSL encrypted · nobleglitch.cloud
        </p>
      </div>
    </div>
  );
};

export default SubscriptionModal;
